import { useEffect, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- option lists and the offers table must not suspend the page that hosts them, and the drain below needs the page-by-page query state
import { useInfiniteQuery } from '@tanstack/react-query';
import { offers } from '@ooh/contracts';
import type { ListOffersQuery, OfferListResponse } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { offerListsQueryKeyPrefix } from '../lib/offer-query-keys';

/**
 * GET /offers is paginated (CU-86cb4wjwk). This hook drains every page automatically (an effect
 * keeps calling `fetchNextPage` while one is available) so callers keep a full-dataset list
 * instead of a lazy-scroll UX.
 */
export function useOffersList(view: ListOffersQuery['view'], assignedToUserIds?: number[]) {
  const api = useApiClient();
  const query = useInfiniteQuery<
    OfferListResponse,
    Error,
    { pages: OfferListResponse[] },
    readonly unknown[],
    string | undefined
  >({
    queryKey: [...offerListsQueryKeyPrefix, { view, assignedToUserIds: assignedToUserIds ?? [] }],
    queryFn: ({ pageParam, signal }) =>
      api.call(offers.list, { view, assignedToUserIds, cursor: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.nextCursor ?? undefined,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = query;
  useEffect(() => {
    // Without the isError gate a permanently failing page (react-query's retries exhausted) would
    // re-fire fetchNextPage forever, because hasNextPage stays true from the last successful page.
    if (hasNextPage && !isFetchingNextPage && !isError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const data = useMemo(() => query.data?.pages.flatMap((page) => page?.data ?? []), [query.data]);

  return {
    ...query,
    data,
    // Stays pending until every page has drained; once isError is true the drain has stopped, so
    // this falls back to false and the caller's error branch takes over instead of a permanent spinner.
    isLoading: query.isLoading || (query.hasNextPage && !query.isError),
  };
}
